import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box } from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";

// First flip after a deliberate pause, then a steady pace while held
const HOLD_MS = 700;
const REPEAT_MS = 1000;
const ZONE_WIDTH = 28;
const ZONE_GAP = 2;

const MONTH_SHORT = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

const fill = keyframes({ from: { height: "0%" }, to: { height: "100%" } });

type Props = {
	currentDate: Date;
	onMonthOffset: (offset: number) => void;
};

/** Shown only while dragging: hold the bar over a side strip and the calendar
 *  turns the page, so a drop can land on any month. The strips sit in the page
 *  gutter, outside the grid, so they never cover a day. */
export const DragMonthZones = ({ currentDate, onMonthOffset }: Props) => {
	const theme = useTheme();
	const [active, setActive] = useState<-1 | 1 | null>(null);
	const [flips, setFlips] = useState(0);
	const onMonthOffsetRef = useRef(onMonthOffset);
	useEffect(() => {
		onMonthOffsetRef.current = onMonthOffset;
	}, [onMonthOffset]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: flips re-arms the timer after each page turn
	useEffect(() => {
		if (active == null) return;
		const id = setTimeout(
			() => {
				onMonthOffsetRef.current(active);
				setFlips((n) => n + 1);
			},
			flips === 0 ? HOLD_MS : REPEAT_MS,
		);
		return () => clearTimeout(id);
	}, [active, flips]);

	const monthLabel = (offset: number) =>
		MONTH_SHORT[
			new Date(
				currentDate.getFullYear(),
				currentDate.getMonth() + offset,
				1,
			).getMonth()
		];

	const zone = (offset: -1 | 1) => {
		const isActive = active === offset;
		const Icon = offset === -1 ? ChevronLeftIcon : ChevronRightIcon;
		return (
			<Box
				onMouseEnter={() => {
					setFlips(0);
					setActive(offset);
				}}
				onMouseLeave={() => setActive(null)}
				sx={{
					position: "absolute",
					top: 0,
					bottom: 0,
					[offset === -1 ? "left" : "right"]: -(ZONE_WIDTH + ZONE_GAP),
					width: ZONE_WIDTH,
					zIndex: 9998,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 0.5,
					overflow: "hidden",
					borderRadius: 1,
					border: `1px dashed ${theme.palette.primary.main}`,
					backgroundColor: alpha(
						theme.palette.primary.main,
						isActive ? 0.16 : 0.06,
					),
					color: "primary.main",
					fontSize: "0.7rem",
					fontWeight: 700,
					letterSpacing: "0.08em",
					textTransform: "uppercase",
					transition: "background-color 0.15s ease",
				}}
			>
				{isActive && (
					<Box
						// restart the sweep after each page turn
						key={flips}
						sx={{
							position: "absolute",
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: alpha(theme.palette.primary.main, 0.22),
							animation: `${fill} ${flips === 0 ? HOLD_MS : REPEAT_MS}ms linear forwards`,
						}}
					/>
				)}
				<Icon sx={{ fontSize: 20, position: "relative" }} />
				<Box sx={{ writingMode: "vertical-rl", position: "relative" }}>
					{monthLabel(offset)}
				</Box>
			</Box>
		);
	};

	return (
		<>
			{zone(-1)}
			{zone(1)}
		</>
	);
};
